import { entityKeys } from '../../lib/entity-queries'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { assistantEffects } from '@/lib/assistant-effects.generated'
import { policyDefinitionKeys, policyOrderApi, policyOrderKeys, policyResultKeys, type PolicyPlacement } from '../../api/policy-order-api'
import { policyCatalogApi, policyCatalogKeys } from '../../api/policy-catalog-api'

export function useSetPolicyEnabled(entityId: string, valueType: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ['policy-enabled', entityId, valueType],
    mutationFn: ({ policyId, expectedRevision, enabled }: { policyId: string; expectedRevision: number; enabled: boolean }) =>
      policyCatalogApi.setEnabled(entityId, valueType, policyId, expectedRevision, enabled),
    onSuccess: async () => {
      const refreshKeys = [policyResultKeys.all(entityId), policyDefinitionKeys.all(entityId),
        entityKeys.settings(entityId), ['assistant-availability'], entityKeys.brandingRoot,
        policyOrderKeys.all(entityId), policyCatalogKeys.all(entityId)]
      await Promise.all(refreshKeys.map((queryKey) => queryClient.cancelQueries({ queryKey })))
      await Promise.all(refreshKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey })))
    },
  })
}

export function usePolicyOrder(entityId: string, valueType: string, enabled = true) {
  return useQuery({ queryKey: policyOrderKeys.type(entityId, valueType), queryFn: () => policyOrderApi.read(entityId, valueType), enabled, meta: { assistantEffects: [assistantEffects.settingsChanged, assistantEffects.preferredCounterPoliciesChanged, assistantEffects.printerEventRulesChanged] } })
}

export function useMovePolicy(entityId: string, valueType: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationKey: ['policy-move', entityId, valueType],
    mutationFn: ({ policyId, placement }: { policyId: string; placement: PolicyPlacement }) => policyOrderApi.move(entityId, valueType, policyId, placement),
    onSuccess: async () => {
      const refreshKeys = [policyResultKeys.all(entityId), policyDefinitionKeys.all(entityId),
        entityKeys.brandingRoot, policyOrderKeys.all(entityId), policyCatalogKeys.all(entityId)]
      await Promise.all(refreshKeys.map((queryKey) => queryClient.cancelQueries({ queryKey })))
      await Promise.all(refreshKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey })))
    },
  })
}
